/**
 * @module @repo/global-login-sdk/dialog-result
 * Gemeinsame Erfolgs-/Ergebnis-Region der einbettbaren Auth-Dialoge:
 * `role="status"`-Live-Region (Icon + Nachricht) mit Schließen-Footer, die beim
 * Mounten den Fokus in den Dialog zurückholt. Vorher in `login-dialog` und
 * `request-access` dupliziert (jscpd ~37 Zeilen).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { DialogButton } from './dialog';

/** Props von {@link DialogResult}. */
export interface DialogResultProps {
  /** Dekoratives Status-Icon (z. B. Häkchen/Umschlag); sollte `aria-hidden` sein. */
  icon: ReactNode;
  /** Bestätigungstext der Live-Region. */
  children: ReactNode;
  /** Schließen-Callback des Dialogs. */
  onClose: () => void;
  /** Beschriftung des Schließen-Buttons. Default „Schließen". */
  closeLabel?: string;
}

/**
 * Erfolgs-/Ergebnis-Region der Auth-Dialoge. `role="status"` (Screenreader-Ansage)
 * und holt beim Mounten den Fokus in den Dialog zurück: sie ersetzt das Formular,
 * dessen Submit-Element unmountet — sonst fiele der Fokus auf `<body>` und Esc
 * (Handler am Dialog-Panel) griffe nicht mehr. Icon + Nachricht + Schließen-Footer
 * sind bewusst gemeinsam gekapselt.
 *
 * @param props Siehe {@link DialogResultProps}.
 * @returns Die gerenderte Ergebnis-Region.
 */
export function DialogResult(props: DialogResultProps): ReactNode {
  const { icon, children, onClose, closeLabel = 'Schließen' } = props;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div ref={ref} role="status" tabIndex={-1} className="flex flex-col gap-4 focus:outline-none">
      <div className="flex items-start gap-3">
        {icon}
        <p className="text-sm text-foreground">{children}</p>
      </div>
      <div className="flex justify-end">
        <DialogButton onClick={onClose}>{closeLabel}</DialogButton>
      </div>
    </div>
  );
}
